// web/src/i18n.ts
// 中英双语文案字典 + 语言切换。用法：<element data-i18n="key">，JS 端 setLang 自动填充。
//
// 首行 import "./shell" 是刻意的：每个页面入口都会 import 本模块，借此保证
// 「左栏导航外壳」在所有页面上都先于页面脚本建立（教程页的 TOC 需要在
// buildToc() 之前就被移入左栏）。shell.ts 不 import 本模块，因此无循环依赖。
import "./shell";

export type Lang = "zh" | "en";

export const I18N: Record<string, { zh: string; en: string }> = {
  // 导航
  "nav.home": { zh: "首页", en: "Home" },
  "nav.quickstart": { zh: "快速上手", en: "Quick Start" },
  "nav.products": { zh: "产品矩阵", en: "Products" },
  "nav.manuals": { zh: "插件手册", en: "Plugin Manuals" },
  "nav.advanced": { zh: "进阶教程", en: "Advanced" },
  "nav.support": { zh: "支持文档", en: "Support" },
  "nav.buy": { zh: "购买授权", en: "Buy License" },
  "nav.lookup": { zh: "查询授权码", en: "Lookup License" },
  "nav.update": { zh: "更新日志", en: "Changelog" },
  "nav.admin": { zh: "后台", en: "Admin" },
  "nav.brand": { zh: "MusicDoll 系列软件", en: "MusicDoll Series" },
  "nav.showcaseOnlyLinks": { zh: "仅外链，无内嵌播放器", en: "Links only, no embedded players" },

  // 首页与购买页共用的价格占位
  "home.priceTbd": { zh: "价格待定", en: "Price TBD" },

  // 购买页
  "buy.title": { zh: "购买授权", en: "Buy License" },
  "buy.lead": {
    zh: "七款软件都有免费版，装好即可直接使用：内置演奏者能跑通从 MIDI 到动画的完整流程，自定义角色等进阶功能需要完整版。完整版 ¥98 / 份，一次性买断，微信支付、支付宝扫码即可。",
    en: "Every app has a free edition you can use right away: bundled performers run the whole MIDI-to-animation pipeline, while custom performers and other advanced features need the full version — $40 USD per app, one-time, paid by card, Apple Pay or Google Pay.",
  },
  "buy.machineLabel": { zh: "机器码", en: "Machine Code" },
  "buy.machineHint": {
    zh: "在软件授权界面复制机器码后粘贴到这里",
    en: "Copy the machine code shown in your app and paste it here",
  },
  "buy.productLabel": {
    zh: "选择产品（可多选）",
    en: "Products (multi-select)",
  },
  "buy.channelLabel": { zh: "支付方式", en: "Payment Method" },
  "buy.channel.wechatpay": {
    zh: "微信支付（官方扫码，自动发码）",
    en: "WeChat Pay (official Native QR, instant license)",
  },
  "buy.channel.xunhupay": {
    zh: "微信支付（虎皮椒扫码，自动发码）",
    en: "WeChat Pay (XunhuPay QR, instant license)",
  },
  "buy.channel.xunhupay_alipay": {
    zh: "支付宝（虎皮椒扫码，自动发码）",
    en: "Alipay (XunhuPay QR, instant license)",
  },
  "buy.channel.waffo": {
    zh: "海外在线支付（Waffo，国际卡/海外方式，自动发码）",
    en: "Waffo (overseas cards / international, instant license)",
  },
  "buy.channel.manual": {
    zh: "人工渠道（淘宝/海淘，作者确认后发码）",
    en: "Manual (Taobao / international, key issued after seller confirmation)",
  },
  "buy.submit": { zh: "提交订单", en: "Place Order" },
  "buy.machineInvalid": {
    zh: "机器码格式不正确（应为大写十六进制，最长 16 位）",
    en: "Invalid machine code (uppercase hex, up to 16 chars)",
  },
  "buy.noProduct": {
    zh: "请至少选择一个产品",
    en: "Please select at least one product",
  },
  "buy.noChannel": {
    zh: "请选择支付方式",
    en: "Please choose a payment method",
  },
  "buy.channelUnavailable": {
    zh: "当前没有可用的在线支付渠道，请发邮件到 highmore9501@163.com，我们人工给你处理。",
    en: "No online payment method is available right now — email highmore9501@163.com and we will handle it manually.",
  },
  "buy.creating": { zh: "正在创建订单…", en: "Creating order…" },
  "buy.resultTitle": {
    zh: "订单已创建，请完成付款",
    en: "Order created — please complete payment",
  },
  "buy.orderNo": { zh: "订单号", en: "Order No." },
  "buy.manualInstruction": {
    zh: "请到淘宝店拍下对应商品，在备注中填写订单号与机器码；作者确认收款后将自动生成授权码（也可稍后到「查询授权码」页查看）。",
    en: 'Please visit the Taobao store, order the product, and write the order number and machine code in the notes. The license key will be issued after the seller confirms payment (you can also check it later on the "Lookup License" page).',
  },
  "buy.goResult": { zh: "等待付款结果 →", en: "Wait for payment result →" },
  "buy.pay": { zh: "去付款", en: "Pay now" },
  "buy.scanQr": {
    zh: "请用微信或支付宝扫下面的二维码完成付款",
    en: "Scan the QR code below with WeChat or Alipay to pay",
  },
  "buy.openPayPage": { zh: "在新页面打开支付", en: "Open payment page" },
  "buy.backendDown": {
    zh: "⚠ 后端尚未部署，当前为界面预览，暂不能真实下单。",
    en: "⚠ Backend not deployed yet — UI preview only, ordering is unavailable.",
  },
  "buy.apiError": { zh: "下单失败：", en: "Order failed: " },

  // 结果页
  "result.title": { zh: "付款结果", en: "Payment Result" },
  "result.ordersTitle": { zh: "订单列表", en: "Orders" },
  "result.noOrders": {
    zh: "未提供订单号，请从购买页进入。",
    en: "No order number provided. Please start from the buy page.",
  },
  "result.waiting": {
    zh: "正在确认付款，请稍候…（页面会自动刷新）",
    en: "Confirming payment… (auto-refreshing)",
  },
  "result.paidTitle": {
    zh: "✅ 付款成功，你的授权码：",
    en: "✅ Payment received — your license key:",
  },
  "result.copy": { zh: "复制授权码", en: "Copy License Key" },
  "result.copied": {
    zh: "已复制！请粘贴到软件中验证",
    en: "Copied! Paste it into your app",
  },
  "result.timeout": {
    zh: "确认超时：若已完成付款请稍后到「查询授权码」页查看，或联系售后。",
    en: 'Confirmation timed out: if you paid, check the "Lookup License" page later or contact support.',
  },
  "result.expired": {
    zh: "订单已过期，请重新下单。",
    en: "The order expired. Please place a new order.",
  },
  "result.refunded": {
    zh: "该订单已退款。",
    en: "This order has been refunded.",
  },
  "result.goLookup": { zh: "前往查询授权码", en: "Go to Lookup License" },
  "result.goBuy": { zh: "重新下单", en: "Place a new order" },
  "result.tipPaidTitle": { zh: "打赏成功，感谢支持！", en: "Thank you for your support!" },
  "result.tipRedirect": {
    zh: "正在前往下载页…（没自动跳转就点下面的按钮）",
    en: "Taking you to the download page… (use the button below if nothing happens)",
  },
  "result.goDownload": { zh: "前往下载页 →", en: "Go to the download page →" },
  "result.tipWaiting": {
    zh: "等待确认打赏…（人工确认通常需要一点时间）",
    en: "Waiting for the tip to be confirmed… (manual confirmation takes a moment)",
  },

  // 免费版下载页（/free/download.html）
  // 七款软件共用一个下载页：中文看百度网盘、英文看 Google Drive。
  // 口径（2026-10-19）：只强调「免费能用」，付费只用一句话带过，不列功能清单。
  "free.title": {
    zh: "下载 MusicDoll 系列软件",
    en: "Download MusicDoll Series",
  },
  "free.lead": {
    zh: "七款软件都能免费下载使用：内置演奏者直接出片，从 MIDI 到动画的完整流程都能跑，不需要授权码。完整版 ¥98 / 份，解锁自定义角色与全部功能。",
    en: "All seven apps are free to download and use: the bundled performers work out of the box, the whole MIDI-to-animation pipeline runs, and no license key is needed. The full version is $40 USD per app, unlocking custom performers and every feature.",
  },
  "free.softTitle": { zh: "软件包", en: "Software packages" },
  "free.softLead": {
    zh: "网盘里是七款软件的发行包（<code>*_dist.zip</code>）。下载后解压即用，免费版不需要授权码。",
    en: "The drive holds the release package for each of the seven apps (<code>*_dist.zip</code>). Unzip and run — the free edition needs no license key.",
  },
  "free.softNames": {
    zh: "Key Ripple（钢琴）、String Flow（弦乐）、Fret Dance（吉他）、Beat Bloom（打击乐）、Zheng Drift（古筝）、Harp Glide（竖琴）、Wind Rise（管乐）。",
    en: "Key Ripple (piano), String Flow (strings), Fret Dance (guitar), Beat Bloom (percussion), Zheng Drift (guzheng), Harp Glide (harp), Wind Rise (wind).",
  },
  "free.projTitle": { zh: "预设工程文件", en: "Preset project files" },
  "free.projLead": {
    zh: "Blender / Unreal 的预设工程，和软件一起用，省去自己搭场景的步骤。",
    en: "Preset Blender / Unreal projects that pair with the apps, so you do not have to build a scene from scratch.",
  },
  "free.note": {
    zh: "免费版不需要授权码；Blender / Unreal 插件也是免费的。",
    en: "The free edition needs no license key, and the Blender / Unreal plugins are free as well.",
  },
  "free.paid": {
    zh: "免费版装好即可直接使用；完整版一次性买断，解锁自定义角色与全部功能。",
    en: "The free edition works out of the box; the full version is a one-time purchase that unlocks custom performers and every feature.",
  },
  "free.priceLabel": { zh: "完整版价格：", en: "Full version: " },
  "free.buyBtn": { zh: "购买完整版 →", en: "Buy the full version →" },
  "free.quickstart": {
    zh: "看 Quick Start 快速上手 →",
    en: "Read the Quick Start →",
  },
  "free.support": {
    zh: "链接失效？到支持文档反馈 →",
    en: "Link broken? Report it in Support →",
  },

  // 查询页
  "lookup.title": { zh: "查询 / 补发授权码", en: "Lookup / Re-issue License" },
  "lookup.machineLabel": { zh: "机器码", en: "Machine Code" },
  "lookup.productLabel": { zh: "产品", en: "Product" },
  "lookup.submit": { zh: "查询", en: "Lookup" },
  "lookup.found": { zh: "授权码：", en: "License key: " },
  "lookup.notFound": {
    zh: "未找到该机器码的授权记录。若已付款仍查不到，请联系售后并提供订单号。",
    en: "No license found for this machine code. If you paid but see nothing, contact support with your order number.",
  },
  "lookup.copy": { zh: "复制", en: "Copy" },
  "lookup.copied": { zh: "已复制", en: "Copied" },

  // 页脚
  "footer.tip": {
    zh: "一机一码，正版授权。购买即代表同意虚拟商品售后政策。",
    en: "One machine, one license. Purchase implies agreement to the virtual-goods policy.",
  },
  "footer.copyright": { zh: "© 音乐人偶系列软件", en: "© Music Doll Series" },
  "footer.terms": { zh: "服务条款", en: "Terms of Service" },
  "footer.privacy": { zh: "隐私政策", en: "Privacy Policy" },
  "footer.refund": { zh: "退款政策", en: "Refund Policy" },
  "footer.contact": { zh: "客服邮箱", en: "Support email" },
  "buy.termsNote": {
    zh: '提交订单即表示你已阅读并同意<a href="/terms.html">服务条款</a>与<a href="/privacy.html">隐私政策</a>。',
    en: 'By placing an order you accept our <a href="/terms.html">Terms of Service</a> and <a href="/privacy.html">Privacy Policy</a>.',
  },

  // 首页（落地页）
  "home.title": { zh: "MusicDoll 系列软件官网", en: "MusicDoll Series — Official Site" },
  "home.eyebrow": { zh: "MusicDoll workflow", en: "MusicDoll workflow" },
  "home.heroTitle": {
    zh: "激发无限：<br />让 MIDI 数据<br />转化为精确演奏",
    en: "Unlock more:<br />turn MIDI data<br />into precise performance",
  },
  "home.heroSub": {
    zh: "告别繁琐的 K 帧，利用 MusicDoll 的精确 MIDI 引擎，轻松生成多角色同步动作，与舞台表达无缝衔接。七款软件都能免费下载使用，内置演奏者直接出片。",
    en: "Skip the tedious keyframing. MusicDoll's precise MIDI engine generates synchronised motion for every performer, ready to blend into your staging. All seven apps are free to download and use, with bundled performers that work out of the box.",
  },
  "home.heroCta": { zh: "产品体验", en: "Explore Products" },
  "home.heroAlt": { zh: "MusicDoll 乐队场景演示", en: "MusicDoll ensemble scene demo" },

  "home.f1t": { zh: "绝佳 MIDI 同步", en: "Rock-solid MIDI sync" },
  "home.f1p": {
    zh: "精准匹配 MIDI 时间轴与节奏，把演奏动作自然落到音乐节拍上。",
    en: "Aligns motion to the MIDI timeline and groove so every gesture lands on the beat.",
  },
  "home.f1alt": { zh: "MIDI 数据视图", en: "MIDI data view" },

  "home.f2t": { zh: "智能 K 帧自动化", en: "Smart keyframe automation" },
  "home.f2p": {
    zh: "自动识别关键时刻，减少重复校准和反复手工修正带来的时间损耗。",
    en: "Detects the key moments automatically, cutting the time lost to repeated calibration and manual fixes.",
  },
  "home.f2alt": { zh: "自动 K 帧演示", en: "Automatic keyframe demo" },

  "home.f3t": { zh: "快速场景角色管理", en: "Fast multi-performer staging" },
  "home.f3p": {
    zh: "支持乐队场景中多角色编排，快速扩展到合奏与舞台演出需求。",
    en: "Arrange multiple performers in one scene and scale quickly to ensemble and stage shots.",
  },
  "home.f3alt": { zh: "场景管理演示", en: "Scene management demo" },

  "home.f4t": { zh: "灵活角色自定义", en: "Flexible performer customization" },
  "home.f4p": {
    zh: "根据项目需要调整角色外观、动作风格与场景配置，保留创作自由度；自定义角色属于完整版功能。",
    en: "Adjust performer looks, motion style and scene setup per project while keeping creative control; custom performers are part of the full version.",
  },
  "home.f4alt": { zh: "角色自定义演示", en: "Performer customization demo" },

  "home.workflowTitle": { zh: "核心工作流", en: "Core workflow" },
  "home.pointsTitle": { zh: "主要特点", en: "Highlights" },
  "home.point1": { zh: "支持多乐器场景协同", en: "Multi-instrument scenes in one take" },
  "home.point2": { zh: "自动化减少手工返工", en: "Automation reduces manual rework" },
  "home.point3": { zh: "节奏与位置更稳定", en: "Consistent timing and placement" },
  "home.point4": { zh: "可由创作者持续打磨效果", en: "Keeps iterating with the creator" },
  "home.galleryTitle": { zh: "展示合集", en: "Showcase" },
  "home.galleryCta": { zh: "查看全部视频链接 →", en: "See all video links →" },
  "home.instrumentsTitle": { zh: "七款乐器", en: "Seven instruments" },
  "home.instrumentCta": { zh: "进入 Quick Start", en: "Open Quick Start" },

  // 角色绑定代工服务页（rigging-service.html）
  // 定位：先劝人自己学（教程免费），再给出「确实搞不定」时的付费兜底。
  // 价格是参考价，最终报价以看到客户模型后的评估为准；政策细节刻意不写死。
  "rig.eyebrow": { zh: "可选服务", en: "Optional service" },
  "rig.title": {
    zh: "角色绑定代工服务",
    en: "Custom Rigging Service",
  },
  "rig.sub": {
    zh: "不想自己改骨骼、接控制器？把角色模型发过来，我按 MusicDoll 的规范把它做成能直接出片的自定义角色。",
    en: "Rather not touch bones and controllers yourself? Send the character model over and I'll turn it into a MusicDoll-ready custom performer you can render with right away.",
  },
  "rig.chipPrice": {
    zh: "参考价 ¥3,000 / 件",
    en: "Reference $500 / character",
  },
  "rig.chipQuote": { zh: "先评估后报价", en: "Quote after inspection" },
  "rig.chipFree": {
    zh: "自己绑是免费的",
    en: "Doing it yourself is free",
  },

  "rig.diyTitle": {
    zh: "先说清楚：自己绑是免费的",
    en: "First, the honest part: rigging it yourself is free",
  },
  "rig.diyP1": {
    zh: '「自定义人物」的完整流程——骨骼预处理、控制体系、关键姿势、导出人物数据——在<a href="/advanced.html">进阶教程</a>里全部公开，配套视频也免费。',
    en: 'The whole "custom performer" pipeline — rig prep, control system, key poses, exporting performer data — is published in the <a href="/advanced.html">advanced tutorial</a>, and the matching videos are free.',
  },
  "rig.diyP2": {
    zh: "如果你有 Blender 或 UE 基础，跟着教程走一遍通常最划算：不花钱，而且以后换角色、改服装、加表情都自己说了算。",
    en: "If you already know Blender or UE, following that tutorial is usually the better deal: no cost, and later on you can swap characters, change outfits and add expressions on your own terms.",
  },
  "rig.diyP3": {
    zh: "这个服务只解决两种情况：",
    en: "This service only solves two situations:",
  },
  "rig.diyList": {
    zh: "<li>项目赶时间，不想花几天去学绑定；</li><li>照教程做过但卡住了——骨骼命名不规范、控制器对不上、手指姿势怎么调都不自然。</li>",
    en: "<li>The deadline is close and you don't want to spend days learning rigging;</li><li>You tried the tutorial and got stuck — bone names don't follow the convention, controllers don't line up, or the finger poses never look right.</li>",
  },
  "rig.diyCta": {
    zh: "先看免费的进阶教程",
    en: "Read the free advanced tutorial",
  },

  "rig.priceTitle": { zh: "价格", en: "Price" },
  "rig.priceAmount": { zh: "¥3,000", en: "$500" },
  "rig.priceUnit": {
    zh: "/ 件（参考价）",
    en: "/ character (reference)",
  },
  "rig.priceP1": {
    zh: "这是参考价，不是一刀切的定价。最终报价要看到模型才能定：骨骼规不规范、手指和控制器是什么结构、要不要改骨骼、服装和头发要不要处理、演奏哪种乐器，每一样都会改变工作量。",
    en: "This is a reference price, not a flat rate. The final quote needs the model in hand: whether the bones are tidy, how fingers and controllers are structured, whether the rig has to be rebuilt, whether clothing and hair need work, and which instrument it plays — each of these changes the workload.",
  },
  "rig.priceP2": {
    zh: "把模型发过来，我先看一遍，再给你确定的报价和周期。看完觉得不合适，不收费，也不用有压力。",
    en: "Send the model, I'll look through it, then give you a firm quote and timeline. If it doesn't suit you, there's no charge and no pressure.",
  },
  "rig.priceDeal": {
    zh: "详情可细谈。",
    en: "Details are open to discussion.",
  },
  "rig.priceDealHint": {
    zh: "报价里的每一项都可以商量，比如你只想解决卡住的那一步。",
    en: "Every line in the quote is negotiable — for example if you only need the one step you're stuck on.",
  },

  "rig.compareTitle": {
    zh: "自己绑，还是找我代工？",
    en: "Rig it yourself, or commission it?",
  },
  "rig.compareTable": {
    zh: `<table class="table"><thead><tr><th></th><th>自己绑</th><th>代工</th></tr></thead><tbody><tr><td>费用</td><td>免费</td><td>¥3,000 / 件（参考价）</td></tr><tr><td>时间</td><td>取决于你的基础，要跟着教程走一遍</td><td>取决于排期，你不用动手</td></tr><tr><td>后续</td><td>换角色、改服装、加表情都自己改</td><td>新角色或大改动需要再来一次</td></tr><tr><td>适合</td><td>有 Blender / UE 基础，或愿意花时间学</td><td>赶项目，或卡在某一步过不去</td></tr></tbody></table>`,
    en: `<table class="table"><thead><tr><th></th><th>Do it yourself</th><th>Commission</th></tr></thead><tbody><tr><td>Cost</td><td>Free</td><td>$500 / character (reference)</td></tr><tr><td>Time</td><td>Depends on your background; you work through the tutorial</td><td>Depends on the queue; you don't touch it</td></tr><tr><td>Later</td><td>New characters, outfits and expressions all stay in your hands</td><td>A new character or a big change means another round</td></tr><tr><td>Best for</td><td>You know Blender / UE, or you're willing to learn</td><td>Deadline pressure, or you're stuck on one step</td></tr></tbody></table>`,
  },

  "rig.getTitle": { zh: "你会拿到什么", en: "What you get" },
  "rig.getLead": {
    zh: "交付的是「这个角色能被软件直接用」，不是建模，也不是动画。",
    en: "What's delivered is that this character works in the app — not modelling, and not animation.",
  },
  "rig.getList": {
    zh: "<li>一个该软件能直接识别的自定义角色文件，导入即可用；</li><li>一份简要的适配说明：这个角色哪些地方可以调、有哪些注意事项；</li><li>交付后的修改范围会在报价时一次性说清楚，以你确认为准。</li>",
    en: "<li>A custom performer file the app reads directly, ready to import;</li><li>A short adaptation note: what is adjustable on this character and what to watch out for;</li><li>The scope of post-delivery changes is stated up front in the quote and confirmed by you.</li>",
  },

  "rig.needTitle": {
    zh: "我需要你提供什么",
    en: "What I need from you",
  },
  "rig.needList": {
    zh: "<li>角色模型文件：<code>.blend</code> 最省事，<code>FBX</code> / <code>OBJ</code> 也可以，但要能读到骨骼；</li><li>如果模型已经有绑定或控制器，一并说明，能省不少工作量；</li><li>想让这个角色演奏哪种乐器，以及大概是什么镜头（近景看手指，还是中景看全身）；</li><li>可选：一张你想要的姿势截图，或一段参考视频。</li>",
    en: "<li>The character model: <code>.blend</code> is easiest; <code>FBX</code> / <code>OBJ</code> work too as long as the bones are readable;</li><li>If the model already has a rig or controllers, tell me — it saves a lot of work;</li><li>Which instrument it should play, and roughly what kind of shot (close on the fingers, or a medium shot of the whole body);</li><li>Optional: a screenshot of the pose you want, or a reference video.</li>",
  },

  "rig.flowTitle": { zh: "流程", en: "How it works" },
  "rig.flowList": {
    zh: `<div class="timeline-item"><strong>1. 发模型：</strong>把模型和说明发过来（淘宝店或客服邮箱都可以）。</div><div class="timeline-item"><strong>2. 我给结论：</strong>能不能做、最终报价、大概周期，一次说清。</div><div class="timeline-item"><strong>3. 确认再付款：</strong>不确认不收费。</div><div class="timeline-item"><strong>4. 我制作并交付：</strong>角色文件 + 适配说明。</div><div class="timeline-item"><strong>5. 你验收：</strong>修改范围按报价时约定的来。</div>`,
    en: `<div class="timeline-item"><strong>1. Send the model:</strong> the file plus a short description (Taobao store or support email both work).</div><div class="timeline-item"><strong>2. I answer:</strong> whether it's doable, the final quote, and a rough timeline — all at once.</div><div class="timeline-item"><strong>3. Confirm, then pay:</strong> no confirmation, no charge.</div><div class="timeline-item"><strong>4. I build and deliver:</strong> the performer file plus the adaptation note.</div><div class="timeline-item"><strong>5. You sign off:</strong> changes follow whatever we agreed in the quote.</div>`,
  },

  "rig.scopeTitle": {
    zh: "不包含什么",
    en: "What's not included",
  },
  "rig.scopeList": {
    zh: "<li>不包含建模：模型要你提供；只有一张设计图的话需要另外商量；</li><li>不包含演奏动画和出片：交付的是「角色可用」，动画仍然用你自己的音乐文件生成；</li><li>不包含软件授权：完整版授权需要单独购买；</li><li>「一件」指一个角色。多角色、换装变体怎么算，按模型情况单独确认。</li>",
    en: "<li>Modelling: you provide the model; if you only have a design drawing, that's a separate conversation;</li><li>Performance animation and rendering: delivery means the character works — the animation still comes from your own music file;</li><li>Software licenses: the full version is bought separately;</li><li>One character means one character. Multiple characters and outfit variants are quoted case by case.</li>",
  },

  "rig.faqTitle": { zh: "常见问题", en: "FAQ" },
  "rig.faqList": {
    zh: `<div class="timeline-item"><strong>为什么是这个价：</strong>一件要从骨骼整理做到控制体系，再逐键记录基准姿势，是重复且耗时的活，不是点一下按钮。价格里大部分买的是这段人工。</div><div class="timeline-item"><strong>能便宜点吗：</strong>可以谈。如果你只是卡在某一步（比如控制器对不上），也许根本不需要整件代工，把卡住的地方说清楚，按需报价。</div><div class="timeline-item"><strong>VRM / MMD / 商城买的模型可以吗：</strong>可以，具体看骨骼规范程度。评估时会给明确结论，不行也会直说。</div><div class="timeline-item"><strong>我需要会 Blender 吗：</strong>不需要。你只要能打开软件、导出模型文件就行。</div><div class="timeline-item"><strong>我就是想自己学：</strong>那就别买，去看进阶教程，视频是免费的。卡住了再来问一句也可以。</div><div class="timeline-item"><strong>多久能好：</strong>看排期，评估时会给具体时间。</div>`,
    en: `<div class="timeline-item"><strong>Why this price:</strong> one character means sorting out the rig, wiring the control system, then recording baseline poses key by key. It's repetitive, slow work, not a button press. Most of the price buys that labour.</div><div class="timeline-item"><strong>Can it be cheaper:</strong> It can be discussed. If you're only stuck on one step (controllers not lining up, say), you may not need the whole job — describe where it breaks and I'll quote just that.</div><div class="timeline-item"><strong>Can I use a VRM / MMD / store-bought model:</strong> Yes, it depends on how conventional the bones are. The assessment gives a clear answer, and if it won't work I'll say so.</div><div class="timeline-item"><strong>Do I need to know Blender:</strong> No. You only need to open the app and export the model file.</div><div class="timeline-item"><strong>I'd rather learn it myself:</strong> Then don't buy this — read the advanced tutorial, the videos are free. You're still welcome to ask when you get stuck.</div><div class="timeline-item"><strong>How long does it take:</strong> Depends on the queue; the assessment gives a concrete date.</div>`,
  },

  "rig.startTitle": { zh: "怎么开始", en: "How to start" },
  "rig.startLead": {
    zh: "直接来问就行：把模型发我，先说清楚你想让它演奏什么乐器。",
    en: "Just ask: send the model and say which instrument you want it to play.",
  },
  "rig.ctaBuy": { zh: "购买授权", en: "Buy license" },
  "rig.ctaTutorial": { zh: "查看进阶教程", en: "Advanced tutorial" },
  "rig.ctaSupport": {
    zh: "联系方式与售后",
    en: "Contact and support",
  },
  "rig.taobao": {
    zh: "淘宝店：animusic.taobao.com",
    en: "Taobao Store: animusic.taobao.com",
  },

  // 指向代工页的入口（购买页 / 进阶教程页 / 支持页各一处）
  "rig.buyEntryTitle": { zh: "想用自己的角色？", en: "Want your own character?" },
  "rig.buyEntryLead": {
    zh: "完整版可以自定义角色。如果不想自己绑骨骼、接控制器，也可以把模型发来由作者代做——参考价 ¥3,000 / 件，先评估后报价，详情可细谈。",
    en: "The full version lets you customize performers. If you'd rather not rig bones and controllers yourself, send the model over and it can be commissioned — reference $500 / character, quoted after inspection, details open to discussion.",
  },
  "rig.buyEntryCta": {
    zh: "查看角色绑定代工服务",
    en: "See the rigging service",
  },
  "rig.advEntryTitle": {
    zh: "卡在绑定这一步？",
    en: "Stuck on the rigging step?",
  },
  "rig.advEntryLead": {
    zh: "这篇教程是免费的，建议先按流程自己走一遍——以后换角色、改服装都方便。如果确实没时间或反复卡住，也可以把模型发来做代工，参考价 ¥3,000 / 件。",
    en: "This tutorial is free — work through it yourself first, since it makes later character swaps and outfit changes easier. If you really have no time, or keep getting stuck, the model can be commissioned instead: reference $500 / character.",
  },
  "rig.advEntryCta": { zh: "查看代工服务", en: "See the rigging service" },
  "rig.faqEntryLabel": {
    zh: "能不能帮我绑角色：",
    en: "Can you rig the character for me:",
  },
  "rig.faqEntryText": {
    zh: '可以，属于付费代工服务，参考价 ¥3,000 / 件，先评估后报价。详见<a href="/rigging-service.html">代工服务页</a>。不想花钱的话，<a href="/advanced.html">进阶教程</a>是免费的。',
    en: 'Yes — that is a paid rigging service, reference $500 / character, quoted after inspection. See the <a href="/rigging-service.html">rigging service page</a>. If you would rather not pay, the <a href="/advanced.html">advanced tutorial</a> is free.',
  },
};

const NAV_KEY_BY_PATH: Record<string, string> = {
  "/": "nav.home",
  "/index.html": "nav.home",
  "/quickstart.html": "nav.quickstart",
  "/products.html": "nav.products",
  "/plugin-manuals.html": "nav.manuals",
  "/advanced.html": "nav.advanced",
  "/support.html": "nav.support",
  "/update.html": "nav.update",
  "/buy.html": "nav.buy",
  "/lookup.html": "nav.lookup",
  "/result.html": "nav.lookup",
  "/admin/": "nav.admin",
  "/admin/index.html": "nav.admin",
};

function ensureLangSwitchButtons(): void {
  document.querySelectorAll<HTMLElement>(".nav").forEach((nav) => {
    if (nav.querySelector("[data-lang-switch]")) return;
    const btn = document.createElement("button");
    btn.className = "lang-btn";
    btn.setAttribute("data-lang-switch", "");
    nav.appendChild(btn);
  });
}

function applyNavText(): void {
  document.querySelectorAll<HTMLElement>(".brand").forEach((brand) => {
    brand.textContent = t("nav.brand");
  });

  document.querySelectorAll<HTMLAnchorElement>(".nav a").forEach((a) => {
    const path = new URL(a.getAttribute("href") || "/", location.origin)
      .pathname;
    const key = NAV_KEY_BY_PATH[path];
    if (!key) return;
    a.textContent = t(key);
  });
}

export function detectLang(): Lang {
  const saved = localStorage.getItem("lang");
  if (saved === "zh" || saved === "en") return saved;
  return navigator.language && navigator.language.toLowerCase().startsWith("zh")
    ? "zh"
    : "en";
}

let current: Lang = detectLang();

export function getLang(): Lang {
  return current;
}

export function t(key: string): string {
  const entry = I18N[key];
  if (!entry) return key;
  return entry[current];
}

/**
 * 应用语言到页面。
 * 支持的声明式属性：
 *   data-i18n            → 设置 textContent
 *   data-i18n-html       → 设置 innerHTML（文案里需要 <br> 等标签时用）
 *   data-i18n-alt        → 设置 alt 属性（图片等）
 *   data-i18n-placeholder → 设置 placeholder 属性
 */
export function applyLang(lang: Lang): void {
  current = lang;
  localStorage.setItem("lang", lang);
  document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";

  applyNavText();

  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    el.textContent = t(el.dataset.i18n || "");
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-html]").forEach((el) => {
    el.innerHTML = t(el.dataset.i18nHtml || "");
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-alt]").forEach((el) => {
    const key = el.dataset.i18nAlt || "";
    const v = I18N[key];
    if (v) el.setAttribute("alt", v[lang]);
  });
  document
    .querySelectorAll<HTMLElement>("[data-i18n-placeholder]")
    .forEach((el) => {
      const key = el.dataset.i18nPlaceholder || "";
      const v = I18N[key];
      if (v) el.setAttribute("placeholder", v[lang]);
    });

  document
    .querySelectorAll<HTMLElement>("[data-lang-switch]")
    .forEach((btn) => {
      btn.textContent = lang === "zh" ? "EN" : "中文";
      btn.setAttribute(
        "aria-label",
        lang === "zh" ? "Switch to English" : "切换到中文",
      );
      btn.setAttribute(
        "title",
        lang === "zh" ? "Switch to English" : "切换到中文",
      );
    });

  document.dispatchEvent(new CustomEvent("langchange", { detail: lang }));
}

/** 页面初始化：应用已存语言 + 绑定语言切换按钮 */
export function initI18n(): void {
  ensureLangSwitchButtons();
  applyLang(current);
  document
    .querySelectorAll<HTMLElement>("[data-lang-switch]")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        applyLang(current === "zh" ? "en" : "zh");
      });
    });
}
